/**
 * BillingPlans — Página pública de planos comercializáveis
 * Fase 5.10 — Usa GET /public/billing/plans (somente planos completos e públicos)
 *
 * Segurança:
 * - Não exibe plano sem preço, ciclo ou política
 * - Não envia valor autoritativo
 * - Usa textContent / JSX nativo (sem dangerouslySetInnerHTML)
 */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { openApi } from "../../services/api";
import { setSignupIntent } from "../../utils/signupIntent";
import "./styles.css";

const formatCurrency = (amountCents, currency) => {
  const amount = amountCents / 100;
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: currency || "BRL" }).format(amount);
  } catch {
    return `R$ ${amount.toFixed(2)}`;
  }
};

const cycleLabel = (cycle) => {
  const labels = {
    monthly: "mês",
    quarterly: "trimestre",
    semiannual: "semestre",
    annual: "ano",
    custom: "período"
  };
  return labels[cycle] || cycle;
};

const PlanCard = ({ plan, selected, onSelect }) => (
  <div
    className={`billingPlan__card${selected ? " billingPlan__card--selected" : ""}`}
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    onClick={() => onSelect(plan)}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect(plan); }}
  >
    <div className="billingPlan__badge">
      {selected && <span className="billingPlan__selectedBadge">Selecionado</span>}
    </div>
    <h2 className="billingPlan__name">{plan.name}</h2>
    {plan.description && <p className="billingPlan__description">{plan.description}</p>}

    <div className="billingPlan__price">
      <span className="billingPlan__amount">{formatCurrency(plan.amountCents, plan.currency)}</span>
      <span className="billingPlan__cycle">/ {cycleLabel(plan.billingCycle)}</span>
    </div>

    {plan.trialEnabled && plan.trialDays > 0 && (
      <div className="billingPlan__trial">
        ✓ {plan.trialDays} dias grátis
      </div>
    )}

    <ul className="billingPlan__features">
      {plan.users > 0 && <li>{plan.users} {plan.users === 1 ? "usuário" : "usuários"}</li>}
      {plan.connections > 0 && <li>{plan.connections} {plan.connections === 1 ? "conexão" : "conexões"} WhatsApp</li>}
      {plan.queues > 0 && <li>{plan.queues} {plan.queues === 1 ? "fila" : "filas"}</li>}
      {plan.useCampaigns && <li>Campanhas</li>}
      {plan.useExternalApi && <li>API Externa</li>}
      {plan.useWhatsapp !== false && <li>WhatsApp</li>}
    </ul>
  </div>
);

const BillingPlansPage = () => {
  const history = useHistory();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    openApi
      .get("/public/billing/plans")
      .then(({ data }) => {
        if (!mounted) return;
        const plans = Array.isArray(data?.data) ? data.data : [];
        setPlans(plans);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setError("Não foi possível carregar os planos. Tente novamente.");
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const handleStartSignup = () => {
    if (!selectedPlan) return;
    const planPublicId = selectedPlan.publicId || selectedPlan.id;
    setSignupIntent({
      planPublicId,
      planName: selectedPlan.name,
      amountCents: selectedPlan.amountCents,
      currency: selectedPlan.currency,
      billingCycle: selectedPlan.billingCycle,
      users: selectedPlan.users,
      connections: selectedPlan.connections,
    });
    history.push("/signup");
  };

  if (loading) {
    return (
      <main className="billingPlans__root">
        <div className="billingPlans__loading" role="status" aria-live="polite">
          <div className="billingPlans__spinner" aria-hidden="true" />
          <p>Carregando planos...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="billingPlans__root">
        <div className="billingPlans__error" role="alert">
          <p>{error}</p>
          <button
            className="billingPlans__btn billingPlans__btn--primary"
            onClick={() => window.location.reload()}
          >
            Tentar novamente
          </button>
        </div>
      </main>
    );
  }

  if (plans.length === 0) {
    return (
      <main className="billingPlans__root">
        <div className="billingPlans__empty">
          <h1>Nenhum plano disponível</h1>
          <p>Entre em contato com nossa equipe comercial.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="billingPlans__root">
      <header className="billingPlans__header">
        <h1 className="billingPlans__title">Escolha seu plano</h1>
        <p className="billingPlans__subtitle">
          Selecione a opção ideal para sua empresa e comece agora.
        </p>
      </header>

      <section
        className="billingPlans__grid"
        aria-label="Planos disponíveis"
        style={{ gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, 1fr)` }}
      >
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            selected={selectedPlan?.id === plan.id}
            onSelect={setSelectedPlan}
          />
        ))}
      </section>

      {plans.length > 1 && (
        <section className="billingPlans__comparison" aria-label="Comparação de planos">
          <h2 className="billingPlans__comparisonTitle">Comparação de recursos</h2>
          <div className="billingPlans__comparisonTable" role="table">
            <div className="billingPlans__comparisonRow billingPlans__comparisonHeader" role="row">
              <div role="columnheader">Recurso</div>
              {plans.map((p) => <div key={p.id} role="columnheader">{p.name}</div>)}
            </div>
            {[
              { label: "Usuários", key: "users", format: (v) => v || "-" },
              { label: "Conexões", key: "connections", format: (v) => v || "-" },
              { label: "Filas", key: "queues", format: (v) => v || "-" },
              { label: "Campanhas", key: "useCampaigns", format: (v) => v ? "✓" : "–" },
              { label: "API Externa", key: "useExternalApi", format: (v) => v ? "✓" : "–" },
              { label: "Trial", key: "trialEnabled", format: (v) => v ? "✓" : "–" }
            ].map(({ label, key, format }) => (
              <div key={key} className="billingPlans__comparisonRow" role="row">
                <div role="cell" className="billingPlans__comparisonLabel">{label}</div>
                {plans.map((p) => (
                  <div key={p.id} role="cell">{format(p[key])}</div>
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="billingPlans__cta">
        <button
          id="billing-plans-start-signup"
          className="billingPlans__btn billingPlans__btn--primary"
          disabled={!selectedPlan}
          onClick={handleStartSignup}
          aria-label={selectedPlan
            ? `Começar com o plano ${selectedPlan.name}`
            : "Selecione um plano para continuar"}
        >
          {selectedPlan
            ? `Começar com ${selectedPlan.name}`
            : "Selecione um plano"}
        </button>
        {selectedPlan && (
          <p className="billingPlans__ctaNote">
            {formatCurrency(selectedPlan.amountCents, selectedPlan.currency)}/{cycleLabel(selectedPlan.billingCycle)}
            {selectedPlan.trialEnabled && selectedPlan.trialDays > 0
              ? ` · ${selectedPlan.trialDays} dias grátis`
              : ""}
          </p>
        )}
      </div>
    </main>
  );
};

export default BillingPlansPage;
